import { Form, Formik, FormikProps } from 'formik';
import { useTranslation } from 'react-i18next';
import { Row, Col, Typography } from 'antd';

import { QuestionWrapper } from '../../components';
import { InputField, RadioField, InputNumberField, SelectField, CheckBoxField, SliderWithNumber } from '../../fields';
import {
  BestOfTempletonAccountFormValues,
  HazardProfileFormValues,
  InvestingExperienceFormValues,
  SelectedInvestmentCategoryOption,
} from '../../models/Forms';
import { I18nNameSpaces, Languages } from '../../services/i18n';
import { SelectOption } from 'common/components/Form/Select';
import { InvestingStrategy } from 'features/form/constants';
import { VolatilityOptions } from '../HazardProfile';
import { MoventumPortfolios, makeBoTAccountCreationSchema } from './schema';

const CurrencyMap = {
  EUR: ' €',
  CZK: ' KČ',
};

interface Props {
  error?: boolean;
  initialValues?: Partial<BestOfTempletonAccountFormValues>;
  investingExperienceValues?: InvestingExperienceFormValues;
  hazard?: HazardProfileFormValues;
  onSubmit: (values: BestOfTempletonAccountFormValues) => void;
  bindSubmit?: (formik: FormikProps<BestOfTempletonAccountFormValues>) => void;
  strategy: { name: string; type: string };
}

export default function BestOfTempletonAccountCreationForm({
  onSubmit,
  initialValues,
  investingExperienceValues,
  hazard,
  error,
  bindSubmit,
  strategy,
}: Props) {
  const { t, i18n } = useTranslation([I18nNameSpaces.Forms, I18nNameSpaces.Common]);
  const schema = makeBoTAccountCreationSchema(t);

  function createPortfolioOptions(): SelectOption[] {
    // Low-risk portfolios
    const options: SelectOption[] = [
      {
        value: MoventumPortfolios.Defensive,
        label: t(`${I18nNameSpaces.Forms}:bestOfTempletonForm.portfolios.${MoventumPortfolios.Defensive}`),
      },
      {
        value: MoventumPortfolios.Balanced,
        label: t(`${I18nNameSpaces.Forms}:bestOfTempletonForm.portfolios.${MoventumPortfolios.Balanced}`),
      },
    ];

    // High-risk portfolios
    if (strategy.type === InvestingStrategy.Dynamic) {
      options.push({
        value: MoventumPortfolios.Dynamic,
        label: t(`${I18nNameSpaces.Forms}:bestOfTempletonForm.portfolios.${MoventumPortfolios.Dynamic}`),
      });

      if (hazard?.c === VolatilityOptions.Unlimited) {
        options.push({
          value: MoventumPortfolios.Offensive,
          label: t(`${I18nNameSpaces.Forms}:bestOfTempletonForm.portfolios.${MoventumPortfolios.Offensive}`),
        });
      }
    }
    return options;
  }

  function handleOnSubmit(values: BestOfTempletonAccountFormValues) {
    onSubmit && onSubmit(values);
  }

  function renderForm(formik: FormikProps<BestOfTempletonAccountFormValues>) {
    if (bindSubmit) {
      bindSubmit(formik);
    }
    const { values } = formik;
    return (
      <Form>
        <QuestionWrapper.Divider>
          <InputField
            name="accountName"
            label={t('bestOfTempletonForm.accountName.label')}
            placeholder={t('bestOfTempletonForm.accountName.placeholder')}
          />
        </QuestionWrapper.Divider>
        <QuestionWrapper.Divider>
          <QuestionWrapper.Title>{t('bestOfTempletonForm.address.title')}</QuestionWrapper.Title>
          <RadioField
            name="useCustomer1Address"
            type="horizontal"
            options={[
              {
                label: t('bestOfTempletonForm.useCustomer1Address.options.useCustomer1'),
                value: true,
              },
              {
                label: t('bestOfTempletonForm.useCustomer1Address.options.useOther'),
                value: false,
              },
            ]}
            label={t('bestOfTempletonForm.useCustomer1Address.label')}
          />
          {!values.useCustomer1Address && (
            <>
              <Row gutter={20}>
                <Col xs={24} md={12}>
                  <InputField
                    name="address.firstName"
                    label={t('bestOfTempletonForm.address.firstName.label')}
                    placeholder={t('bestOfTempletonForm.address.firstName.placeholder')}
                  />
                </Col>
                <Col xs={24} md={12}>
                  <InputField
                    name="address.lastName"
                    label={t('bestOfTempletonForm.address.lastName.label')}
                    placeholder={t('bestOfTempletonForm.address.lastName.placeholder')}
                  />
                </Col>
              </Row>
              <Row gutter={20}>
                <Col xs={24} md={16}>
                  <InputField
                    name="address.street"
                    label={t('bestOfTempletonForm.address.street.label')}
                    placeholder={t('bestOfTempletonForm.address.street.placeholder')}
                  />
                </Col>
                <Col xs={24} md={8}>
                  <InputField
                    name="address.streetNumber"
                    label={t('bestOfTempletonForm.address.streetNumber.label')}
                    placeholder={t('bestOfTempletonForm.address.streetNumber.placeholder')}
                  />
                </Col>
              </Row>
              <Row gutter={20}>
                <Col xs={24} md={6}>
                  <InputField
                    name="address.psc"
                    label={t('bestOfTempletonForm.address.psc.label')}
                    placeholder={t('bestOfTempletonForm.address.psc.placeholder')}
                  />
                </Col>
                <Col xs={24} md={10}>
                  <InputField
                    name="address.city"
                    label={t('bestOfTempletonForm.address.city.label')}
                    placeholder={t('bestOfTempletonForm.address.city.placeholder')}
                  />
                </Col>
                <Col xs={24} md={8}>
                  <InputField
                    name="address.state"
                    label={t('bestOfTempletonForm.address.state.label')}
                    placeholder={t('bestOfTempletonForm.address.state.placeholder')}
                  />
                </Col>
              </Row>
            </>
          )}
        </QuestionWrapper.Divider>
        <QuestionWrapper.Divider>
          <QuestionWrapper.Title>{t('bestOfTempletonForm.bankAccount.title')}</QuestionWrapper.Title>
          <InputField
            name="bankAccount.accountOwnerName"
            label={t('bestOfTempletonForm.bankAccount.accountOwnerName.label')}
            placeholder={t('bestOfTempletonForm.bankAccount.accountOwnerName.placeholder')}
          />
          <Row gutter={20}>
            <Col xs={24} md={16}>
              <InputField
                name="bankAccount.IBAN"
                label={t('bestOfTempletonForm.bankAccount.IBAN.label')}
                placeholder={t('bestOfTempletonForm.bankAccount.IBAN.placeholder')}
              />
            </Col>
            <Col xs={24} md={8}>
              <InputField
                name="bankAccount.Swift"
                label={t('bestOfTempletonForm.bankAccount.Swift.label')}
                placeholder={t('bestOfTempletonForm.bankAccount.Swift.placeholder')}
              />
            </Col>
          </Row>
          <Row gutter={20}>
            <Col xs={24} md={12}>
              <InputField
                name="bankAccount.bankName"
                label={t('bestOfTempletonForm.bankAccount.bankName.label')}
                placeholder={t('bestOfTempletonForm.bankAccount.bankName.placeholder')}
              />
            </Col>
            <Col xs={24} md={12}>
              <InputField
                name="bankAccount.state"
                label={t('bestOfTempletonForm.bankAccount.state.label')}
                placeholder={t('bestOfTempletonForm.bankAccount.state.placeholder')}
              />
            </Col>
          </Row>
        </QuestionWrapper.Divider>
        <QuestionWrapper.Divider>
          <QuestionWrapper.Title>{t('bestOfTempletonForm.experienceInSelectedCategory.title')}</QuestionWrapper.Title>
          <RadioField
            breakLabel
            name="selectedInvestmentCategory"
            type="vertical"
            options={[
              {
                label: t(
                  `bestOfTempletonForm.selectedInvestmentCategory.options.${SelectedInvestmentCategoryOption.growth}`,
                ),
                value: SelectedInvestmentCategoryOption.growth,
              },
              {
                label: t(
                  `bestOfTempletonForm.selectedInvestmentCategory.options.${SelectedInvestmentCategoryOption.dynamic}`,
                ),
                value: SelectedInvestmentCategoryOption.dynamic,
              },
              {
                label: t(
                  `bestOfTempletonForm.selectedInvestmentCategory.options.${SelectedInvestmentCategoryOption.speculative}`,
                ),
                value: SelectedInvestmentCategoryOption.speculative,
              },
            ]}
            label={t('bestOfTempletonForm.selectedInvestmentCategory.label')}
          />
          <RadioField
            name="experienceInSelectedCategory"
            type="vertical"
            options={[
              {
                label: t('bestOfTempletonForm.experienceInSelectedCategory.options.lt1'),
                value: 'lt1',
              },
              {
                label: t('bestOfTempletonForm.experienceInSelectedCategory.options.lt5'),
                value: 'lt5',
              },
              {
                label: t('bestOfTempletonForm.experienceInSelectedCategory.options.gt5'),
                value: 'gt5',
              },
            ]}
            label={t('bestOfTempletonForm.experienceInSelectedCategory.label')}
          />
        </QuestionWrapper.Divider>
        <QuestionWrapper.Divider>
          <QuestionWrapper.Title>{t('bestOfTempletonForm.avgNumberOfYearlyTransactions.title')}</QuestionWrapper.Title>
          <Row gutter={20}>
            <Col xs={24} md={12}>
              <InputNumberField
                name="avgNumberOfYearlyTransactions"
                label={t('bestOfTempletonForm.avgNumberOfYearlyTransactions.label')}
                precision={0}
                disabled
              />
            </Col>
            <Col xs={24} md={12}>
              <InputNumberField
                name="avgValueOfYearlyTransactions"
                label={t('bestOfTempletonForm.avgValueOfYearlyTransactions.label')}
                disabled
              />
            </Col>
          </Row>
        </QuestionWrapper.Divider>
        <QuestionWrapper.Divider>
          <QuestionWrapper.Title>{t('bestOfTempletonForm.chosenPortfolio.title')}</QuestionWrapper.Title>
          <SelectField
            name="chosenPortfolio"
            options={createPortfolioOptions()}
            label={t('bestOfTempletonForm.chosenPortfolio.label')}
          />
        </QuestionWrapper.Divider>
        <QuestionWrapper.Divider>
          <QuestionWrapper.Title>{t('bestOfTempletonForm.plannedInvestmentDeposit.title')}</QuestionWrapper.Title>
          <Row style={{ alignItems: 'center' }}>
            <Col span={4}>
              {i18n.language === Languages.Czech && (
                <SelectField
                  name="plannedInvestmentDepositCurrency"
                  options={[
                    { label: 'CZK', value: 'CZK' },
                    { label: 'EUR', value: 'EUR' },
                  ]}
                  label={t('bestOfTempletonForm.currency')}
                />
              )}
            </Col>
            <Col span={20} style={{ marginTop: '12px' }}>
              <SliderWithNumber
                name="plannedInvestmentDeposit"
                label={t('bestOfTempletonForm.plannedInvestmentDeposit.label')}
                tooltip={t('bestOfTempletonForm.plannedInvestmentDeposit.tooltip')}
                currencySymbol={CurrencyMap[values.plannedInvestmentDepositCurrency as keyof typeof CurrencyMap]}
                defaultValue={0}
              />
            </Col>
          </Row>
        </QuestionWrapper.Divider>
        <QuestionWrapper.Divider>
          <QuestionWrapper.Title>{t('bestOfTempletonForm.plannedWithdrawal.title')}</QuestionWrapper.Title>
          <Row style={{ alignItems: 'center' }}>
            <Col span={4}>
              {i18n.language === Languages.Czech && (
                <SelectField
                  name="plannedWithdrawalCurrency"
                  options={[
                    { label: 'CZK', value: 'CZK' },
                    { label: 'EUR', value: 'EUR' },
                  ]}
                  label={t('bestOfTempletonForm.currency')}
                />
              )}
            </Col>
            <Col span={20} style={{ marginTop: '12px' }}>
              <SliderWithNumber
                name="plannedWithdrawal"
                label={t('bestOfTempletonForm.plannedWithdrawal.label')}
                tooltip={t('bestOfTempletonForm.plannedWithdrawal.tooltip')}
                currencySymbol={CurrencyMap[values.plannedWithdrawalCurrency as keyof typeof CurrencyMap]}
                defaultValue={0}
              />
            </Col>
          </Row>
        </QuestionWrapper.Divider>
        <QuestionWrapper.Divider>
          <CheckBoxField
            name="transferingFromFranklinTempleton"
            label={t('bestOfTempletonForm.transferingFromFranklinTempleton.label')}
          />
          {values.transferingFromFranklinTempleton ? (
            <InputField
              name="franklinTempletonDepositNumber"
              label={t('bestOfTempletonForm.franklinTempletonDepositNumber.label')}
            />
          ) : null}
        </QuestionWrapper.Divider>
        {error ? <Typography.Paragraph type="danger">{t('clientForm.serverError')}</Typography.Paragraph> : null}
      </Form>
    );
  }

  function getAvgNumberOfYearlyTransactions() {
    let b = investingExperienceValues?.b?.c ?? '0';
    let c = investingExperienceValues?.c?.c ?? '0';

    // The value can still be empty string
    if (b.length === 0) {
      b = '0';
    }

    if (c.length === 0) {
      c = '0';
    }

    return Number.parseInt(b) + Number.parseInt(c);
  }

  function getAvgValueOfYearlyTransactions() {
    let b = investingExperienceValues?.b?.d ?? '0';
    let c = investingExperienceValues?.c?.d ?? '0';

    // The value can still be empty string
    if (b.length === 0) {
      b = '0';
    }

    if (c.length === 0) {
      c = '0';
    }

    return Number.parseFloat(b) + Number.parseFloat(c);
  }

  return (
    <Formik
      initialValues={{
        accountName: initialValues?.accountName ?? '',
        useCustomer1Address: initialValues?.useCustomer1Address ?? true,
        address: {
          firstName: initialValues?.address?.firstName ?? '',
          lastName: initialValues?.address?.lastName ?? '',
          street: initialValues?.address?.street ?? '',
          streetNumber: initialValues?.address?.streetNumber ?? '',
          city: initialValues?.address?.city ?? '',
          psc: initialValues?.address?.psc ?? '',
          state: initialValues?.address?.state ?? '',
        },
        bankAccount: {
          accountOwnerName: initialValues?.bankAccount?.accountOwnerName ?? '',
          IBAN: initialValues?.bankAccount?.IBAN ?? '',
          Swift: initialValues?.bankAccount?.Swift ?? '',
          bankName: initialValues?.bankAccount?.bankName ?? '',
          state: initialValues?.bankAccount?.state ?? '',
        },
        selectedInvestmentCategory:
          initialValues?.selectedInvestmentCategory ??
          (strategy.type === InvestingStrategy.Dynamic
            ? SelectedInvestmentCategoryOption.dynamic
            : SelectedInvestmentCategoryOption.growth),
        experienceInSelectedCategory: initialValues?.experienceInSelectedCategory ?? 'lt1',
        avgNumberOfYearlyTransactions: getAvgNumberOfYearlyTransactions(),
        avgValueOfYearlyTransactions: getAvgValueOfYearlyTransactions(),
        chosenPortfolio: initialValues?.chosenPortfolio ?? '',
        plannedInvestmentDeposit: initialValues?.plannedInvestmentDeposit ?? null,
        plannedInvestmentDepositCurrency:
          initialValues?.plannedInvestmentDepositCurrency ?? (i18n.language === Languages.Czech ? 'CZK' : 'EUR'),
        plannedWithdrawal: initialValues?.plannedWithdrawal ?? null,
        plannedWithdrawalCurrency:
          initialValues?.plannedWithdrawalCurrency ?? (i18n.language === Languages.Czech ? 'CZK' : 'EUR'),
        transferingFromFranklinTempleton: initialValues?.transferingFromFranklinTempleton ?? false,
        franklinTempletonDepositNumber: initialValues?.franklinTempletonDepositNumber ?? '',
      }}
      onSubmit={handleOnSubmit}
      validationSchema={schema}
      validateOnBlur={false}
      validateOnChange={false}
      enableReinitialize
    >
      {renderForm}
    </Formik>
  );
}
